import { lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/sonner";
import { useEffect } from "react";
import { TooltipProvider } from "@/components/ui/tooltip";
import NotFound from "@/pages/NotFound";
import { Route, Switch, useLocation } from "wouter";
import ErrorBoundary from "./components/ErrorBoundary";
import { ThemeProvider } from "./contexts/ThemeContext";
import { LanguageProvider, useLanguage } from "./contexts/LanguageContext";
import Home from "./pages/Home";
import { StructuredData } from "./components/StructuredData";
import { GeoSchema } from "./components/GeoSchema";
import { ChatBot } from "./components/ChatBot";
import { trackSpaPageView } from "./lib/acquisitionTracking";
import { PrivacyPolicyLinks } from "./components/PrivacyPolicyLinks";
import { LanguageSwitchProvider } from "./components/LanguageSwitcher";

// Code-split heavy pages for better initial load performance
const WhyBellaRose = lazy(() => import("./pages/WhyBellaRose"));
const BlogIndex = lazy(() => import("./pages/BlogIndex"));
const BlogArticle = lazy(() => import("./pages/BlogArticle"));
const GuidesIndex = lazy(() => import("./pages/GuidesIndex"));
const Guide = lazy(() => import("./pages/Guide"));
const Offers = lazy(() => import("./pages/Offers"));
const Services = lazy(() => import("./pages/Services"));
const FaqIndex = lazy(() => import("./pages/FaqIndex"));
const FaqService = lazy(() => import("./pages/FaqService"));
const ChatAnalytics = lazy(() => import("./pages/ChatAnalytics"));
const NewsIndex = lazy(() => import("./pages/NewsIndex"));
const NewsArticle = lazy(() => import("./pages/NewsArticle"));
const Reviews = lazy(() => import("./pages/Reviews"));
const DrSamiraHairTransplant = lazy(() => import("./pages/DrSamiraHairTransplant"));
const Citations = lazy(() => import("./pages/Citations"));
const DoctorProfile = lazy(() => import("./pages/DoctorProfile"));
const Doctors = lazy(() => import("./pages/Doctors"));
const BestDentalClinicDubai = lazy(() => import("./pages/BestDentalClinicDubai"));
const BestDermatologyClinicsDubai = lazy(() => import("./pages/BestDermatologyClinicsDubai"));
const WeightLossProgramsDubai = lazy(() => import("./pages/WeightLossProgramsDubai"));
const LaserHairRemovalDubai = lazy(() => import("./pages/LaserHairRemovalDubai"));
const CosmeticSurgeryClinicsDubai = lazy(() => import("./pages/CosmeticSurgeryClinicsDubai"));
const AdminBookings = lazy(() => import("./pages/AdminBookings"));
const AdminChats = lazy(() => import("./pages/AdminChats"));
const OrganicGrowthDashboard = lazy(() => import("./pages/OrganicGrowthDashboard"));
const AdsWeightManagement = lazy(() => import("./pages/AdsWeightManagement"));
const AdsEndolift = lazy(() => import("./pages/AdsEndolift"));
const AdsHollywoodSmile = lazy(() => import("./pages/AdsHollywoodSmile"));
const AdsPrivacyNotice = lazy(() => import("./pages/AdsPrivacyNotice"));
const ApprovedPilotPage = lazy(() => import("./pages/ApprovedPilotPage"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));

// Loading fallback
function PageLoader() {
  const { lang } = useLanguage();
  return (
    <div
      className="min-h-screen bg-[#FBF8F1] flex items-center justify-center"
      role="status"
      aria-live="polite"
      aria-label={lang === "ar" ? "جارٍ تحميل الصفحة" : "Loading page"}
    >
      <div className="flex flex-col items-center gap-4">
        <div className="w-10 h-10 border-2 border-[#D4A843] border-t-transparent rounded-full animate-spin" />
        <p className="text-[#5C5549] text-sm tracking-wider">
          {lang === "ar" ? "جارٍ تحميل الصفحة…" : "Loading page…"}
        </p>
      </div>
    </div>
  );
}
function Router() {
  // make sure to consider if you need authentication for certain routes
  return (
    <Switch>
      <Route path={"/"} component={Home} />
      <Route path={"/why-bella-rose"} component={WhyBellaRose} />
      <Route path={"/blog"} component={BlogIndex} />
      <Route path={"/blog/best-endolift-clinic-dubai"} component={ApprovedPilotPage} />
      <Route path={"/blog/body-contouring-non-surgical-dubai"} component={ApprovedPilotPage} />
      <Route path={"/blog/clear-aligners-vs-braces-dubai-orthodontics"} component={ApprovedPilotPage} />
      <Route path={"/blog/skin-booster-hydration-treatment-dubai"} component={ApprovedPilotPage} />
      <Route path={"/blog/:slug"} component={BlogArticle} />
      <Route path={"/guides"} component={GuidesIndex} />
      <Route path={"/guides/g-cell-hair-treatment-dubai"} component={ApprovedPilotPage} />
      <Route path={"/guides/iv-drip-therapy-dubai"} component={ApprovedPilotPage} />
      <Route path={"/guides/hijama-cupping-therapy-guide-dubai"} component={ApprovedPilotPage} />
      <Route path={"/guides/gum-botox-dubai"} component={ApprovedPilotPage} />
      <Route path={"/guides/:slug"} component={Guide} />
      <Route path={"/offers"} component={Offers} />
      <Route path={"/services"} component={Services} />
      <Route path={"/faq"} component={FaqIndex} />
      <Route path={"/faq/hijama-wet-cupping-dubai"} component={ApprovedPilotPage} />
      <Route path={"/faq/:serviceSlug"} component={FaqService} />
      <Route path={"/news"} component={NewsIndex} />
      <Route path={"/news/:slug"} component={NewsArticle} />
      <Route path={"/reviews"} component={Reviews} />
      <Route path={"/dr-samira-hair-transplant"} component={DrSamiraHairTransplant} />
      <Route path={"/citations"} component={Citations} />
      <Route path={"/privacy-policy"} component={PrivacyPolicy} />
      <Route path={"/doctors"} component={Doctors} />
      <Route path={"/doctors/:slug"} component={DoctorProfile} />
      <Route path={"/best-dental-clinic-dubai"} component={BestDentalClinicDubai} />
      <Route path={"/best-dermatology-clinics-dubai"} component={BestDermatologyClinicsDubai} />
      <Route path={"/weight-loss-programs-dubai"} component={WeightLossProgramsDubai} />
      <Route path={"/laser-hair-removal-dubai"} component={LaserHairRemovalDubai} />
      <Route path={"/cosmetic-surgery-clinics-dubai"} component={CosmeticSurgeryClinicsDubai} />
      <Route path={"/chat-analytics"} component={ChatAnalytics} />
      <Route path={"/admin/bookings"} component={AdminBookings} />
      <Route path={"/admin/chats"} component={AdminChats} />
      <Route path={"/admin/organic-growth"} component={OrganicGrowthDashboard} />
      <Route path={"/ads/weight-management-dubai"} component={AdsWeightManagement} />
      <Route path={"/ads/endolift-dubai"} component={AdsEndolift} />
      <Route path={"/ads/hollywood-smile-dubai"} component={AdsHollywoodSmile} />
      <Route path={"/ads/privacy"} component={AdsPrivacyNotice} />
      <Route path={"/services/endolift"} component={ApprovedPilotPage} />
      <Route path={"/weight-loss"} component={ApprovedPilotPage} />
      <Route path={"/hair-transplant"} component={ApprovedPilotPage} />
      <Route path={"/dental"} component={DepartmentHub} />
      <Route path={"/dermatology"} component={DepartmentHub} />
      <Route path={"/laser-treatments"} component={DepartmentHub} />
      <Route path={"/hair-transplant-dubai"} component={DepartmentHub} />
      <Route path={"/botox-dubai"} component={DepartmentHub} />
      <Route path={"/dental-implants-dubai"} component={DepartmentHub} />
      <Route path={"/404"} component={NotFound} />
      <Route component={NotFound} />
    </Switch>
  );
}

function App() {
  const [location] = useLocation();
  const isHealthAdsRoute = location.startsWith("/ads/") || location === "/dr-samira-hair-transplant";
  const isPrivateRoute = location.startsWith("/admin/") || location === "/chat-analytics";

  useEffect(() => {
    trackSpaPageView(location);
    (window as Window & { bellaRoseTrackAhrefsPageView?: () => void }).bellaRoseTrackAhrefsPageView?.();
  }, [location]);

  return (
    <ErrorBoundary>
      <ThemeProvider defaultTheme="light">
        <LanguageProvider>
          <TooltipProvider>
            <Toaster />
            {!isHealthAdsRoute && !isPrivateRoute && <StructuredData />}
            {!isHealthAdsRoute && !isPrivateRoute && <GeoSchema />}
            <LanguageSwitchProvider>
              <Suspense fallback={<PageLoader />}>
                <Router />
              </Suspense>
            </LanguageSwitchProvider>
            {!isHealthAdsRoute && !isPrivateRoute && location !== "/" && location !== "/privacy-policy" && <PrivacyPolicyLinks />}
            {!isHealthAdsRoute && !isPrivateRoute && <ChatBot />}
          </TooltipProvider>
        </LanguageProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
const DepartmentHub = lazy(() => import("./pages/DepartmentHub"));
